import React from 'react';
import { useParams } from 'react-router-dom';
import { getMetier, getAllMetiers } from '../data/metiers';
import MetierShowcase from '../templates/MetierShowcase';
import MetierInscription from '../templates/MetierInscription';
import MetierB2B from '../templates/MetierB2B';

/**
 * Générateur de routes dynamiques pour les métiers
 * 
 * Utilisation dans App.js :
 * 
 * import { generateMetierRoutes } from './routes/metierRoutes';
 * 
 * <Routes>
 *   {generateMetierRoutes()}
 *   ... autres routes
 * </Routes>
 */

// Composant wrapper pour Showcase
export const MetierShowcaseWrapper = ({ metierId }) => {
  const metier = getMetier(metierId);
  return <MetierShowcase metier={metier} />;
};

// Composant wrapper pour Inscription
export const MetierInscriptionWrapper = ({ metierId }) => {
  const metier = getMetier(metierId);
  return <MetierInscription metier={metier} />;
};

// Composant wrapper pour B2B
export const MetierB2BWrapper = ({ metierId }) => {
  const metier = getMetier(metierId);
  if (!metier) return <div>Métier introuvable</div>;
  // Vérifie si le métier est vraiment prêt pour B2B
  const b2bReady = metier.b2bEnabled && metier.b2b && metier.b2b.titre && Array.isArray(metier.b2b.pricing) && metier.b2b.pricing.length > 0 && Array.isArray(metier.b2b.arguments) && metier.b2b.arguments.length > 0;
  if (!b2bReady) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Section B2B bientôt disponible pour les {metier.nomPluriel}.</div>;
  }
  return <MetierB2B metier={metier} />;
};

// Wrappers basés sur les paramètres d'URL (pour App.js)
export const MetierShowcaseParam = () => {
  const { metierId } = useParams();
  return <MetierShowcaseWrapper metierId={metierId} />;
};

export const MetierInscriptionParam = () => {
  const { metierId } = useParams();
  return <MetierInscriptionWrapper metierId={metierId} />;
};

export const MetierB2BParam = () => {
  const { metierId } = useParams();
  return <MetierB2BWrapper metierId={metierId} />;
};

// Liste des métiers actifs
export const METIERS_ACTIFS = [
  'electriciens',
  'plombiers',
  'garagistes',
  'menuisiers',
  'macons',
  'carreleurs',
  'couvreurs',
  'peintres',
  'jardiniers',
  'ramoneurs',
  'coiffeurs',
  'estheticiens',
  'fleuristes',
  'carrossiers',
  'controle_technique'
];

// Génère les routes JSX pour tous les métiers
export const generateMetierRoutes = () => {
  // Utilise la liste centralisée pour éviter divergences
  const all = getAllMetiers();
  const routes = [];
  all.forEach(metier => {
    const { id } = metier;
    routes.push({
      path: `/${id}`,
      element: <MetierShowcaseWrapper metierId={id} />,
      key: `showcase-${id}`
    });
    routes.push({
      path: `/inscription-${id}`,
      element: <MetierInscriptionWrapper metierId={id} />,
      key: `inscription-${id}`
    });
    // Ajoute la route B2B seulement si activée
    if (metier.b2bEnabled) {
      routes.push({
        path: `/${id}-b2b`,
        element: <MetierB2BWrapper metierId={id} />,
        key: `b2b-${id}`
      });
    }
  });
  return routes;
};

// Génère les routes pour environnement de test avec suffixe -new
export const generateMetierTestRoutes = () => {
  const all = getAllMetiers();
  const routes = [];
  all.forEach(metier => {
    const { id } = metier;
    routes.push({
      path: `/${id}-new`,
      element: <MetierShowcaseWrapper metierId={id} />,
      key: `showcase-new-${id}`
    });
    routes.push({
      path: `/inscription-${id}-new`,
      element: <MetierInscriptionWrapper metierId={id} />,
      key: `inscription-new-${id}`
    });
    if (metier.b2bEnabled) {
      routes.push({
        path: `/${id}-b2b-new`,
        element: <MetierB2BWrapper metierId={id} />,
        key: `b2b-new-${id}`
      });
    }
  });
  return routes;
};

// Export individuel des wrappers pour usage manuel si besoin
export default {
  MetierShowcaseWrapper,
  MetierInscriptionWrapper,
  MetierB2BWrapper,
  MetierShowcaseParam,
  MetierInscriptionParam,
  MetierB2BParam,
  generateMetierRoutes,
  generateMetierTestRoutes,
  METIERS_ACTIFS
};
